import { useContext, createContext, useEffect, useMemo, useState } from "react";
import { Asset, TradingPair, useAce } from "@archax/web-lib";
import { getEnv } from "@ace/env";

import { useProducts } from "./use-products";
import { Fund, getFunds } from "../../services/get-funds";
import {
  AltTradingPair,
  getAllTradingPairs,
} from "../../services/get-all-trading-pairs";
import { LocalStorage } from "../../utils/local-storage";
import { localStorageKeys } from "../../constants";
import { arrayToMap } from "../../utils/array-to-map";
import { isValidISIN } from "../../utils/check-isin";

export interface ProductItem {
  externalId: string;
  id: number;
  type: "Trading Pair" | "Fund";
  name: string;
  description: string;
  baseAsset: string;
  quoteAsset: string;
  productId: number;
  productType: string;
  tradingPair: TradingPair | AltTradingPair;
}

const DEFAULT_TRADING_PAIR = getEnv("VITE_ACE_WEB_DEFAULT_TRADING_PAIR");

interface ProductSelectorContextValue {
  products: ProductItem[];
  tradingPairs: TradingPair[];
  altTradingPairs: AltTradingPair[];
  funds: Fund[];
  hasInvestRoleOnly: () => boolean;
  hasTraderRoleOnly: () => boolean;
  hasTraderAndInvestRoles: () => boolean;
  loading: boolean;
  updateSelectedProduct: (
    productId: string,
    options?: { dontPersist?: boolean },
  ) => void;
  selectedProduct: ProductItem | null;
  baseAsset: Asset | null;
  quoteAsset: Asset | null;
  tradingPair: AltTradingPair | TradingPair | null;
  updateSelectedTradingPair: (
    tradingPairId: number,
    options?: { dontPersist?: boolean },
  ) => void;
}

const ProductSelectorContext = createContext<ProductSelectorContextValue>(
  {} as ProductSelectorContextValue,
);
const ProductSelectorContextProvider = ProductSelectorContext.Provider;

interface ProductSelectorProviderProps {
  children: React.ReactNode;
}

const ProductSelectorProvider = ({
  children,
}: ProductSelectorProviderProps) => {
  const { data, permissionsInfo } = useAce({
    topics: ["trading-pairs", "assets"],
  });
  const { products: productTypes, loadingProducts } = useProducts();
  const [funds, setFunds] = useState<Fund[]>([]);
  const [altTradingPairs, setAltTradingPairs] = useState<AltTradingPair[]>([]);
  const [fundsLoading, setFundsLoading] = useState(true);
  const [altTradingPairsLoading, setAltTradingPairsLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<ProductItem | null>(
    null,
  );
  const [baseAsset, setBaseAsset] = useState<Asset | null>(null);
  const [quoteAsset, setQuoteAsset] = useState<Asset | null>(null);

  const loading = useMemo(() => {
    return (
      fundsLoading ||
      altTradingPairsLoading ||
      !data["trading-pairs"] ||
      loadingProducts ||
      !data["assets"]
    );
  }, [
    fundsLoading,
    altTradingPairsLoading,
    !data["assets"],
    data["trading-pairs"],
    loadingProducts,
  ]);

  const productTypesMap = useMemo(() => {
    return arrayToMap(productTypes, "productId");
  }, [productTypes]);

  const altTradingPairsMap = useMemo(() => {
    return arrayToMap(altTradingPairs, "id");
  }, [altTradingPairs]);

  const tradingPair = useMemo(() => {
    if (!selectedProduct || selectedProduct.type !== "Trading Pair") {
      return (
        data["trading-pairs"]?.[
          localStorage.getItem(localStorageKeys.lastTradingPairSelected) ||
            getEnv("VITE_ACE_WEB_DEFAULT_TRADING_PAIR")
        ] || null
      );
    }
    return selectedProduct.tradingPair;
  }, [
    selectedProduct,
    localStorage.getItem(localStorageKeys.lastTradingPairSelected),
    data["trading-pairs"],
  ]);

  useEffect(() => {
    const fetchFunds = async () => {
      try {
        const fetchedFunds = await getFunds();
        const filteredFunds = fetchedFunds.filter(
          (f) => f.status === "Subscribe",
        );
        setFunds(filteredFunds);
        setFundsLoading(false);
      } catch (error) {
        setFundsLoading(false);
        console.error("Error fetching funds:", error);
      }
    };

    const fetchTradingPairs = async () => {
      try {
        const fetchedTradingPairs = await getAllTradingPairs();
        const filteredTradingPairs = fetchedTradingPairs;
        setAltTradingPairs(filteredTradingPairs);
        setAltTradingPairsLoading(false);
      } catch (error) {
        setAltTradingPairsLoading(false);
        console.error("Error fetching trading pairs:", error);
      }
    };

    fetchFunds();
    fetchTradingPairs();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (hasInvestRoleOnly()) {
      const lastSelectedProductId =
        LocalStorage.getItem(localStorageKeys.lastProductSelected) ||
        `TP-${DEFAULT_TRADING_PAIR}`;
      return updateSelectedProduct(
        lastSelectedProductId || products[0]?.externalId || "",
      );
    }
    updateSelectedProduct(
      LocalStorage.getItem(localStorageKeys.lastProductSelected) ||
        `TP-${DEFAULT_TRADING_PAIR}`,
    );
  }, [loading]);

  const getBaseAssetSymbol = (tradingPairId: number) => {
    const assets = data["assets"];
    if (altTradingPairs.length === 0) {
      return "";
    }
    const baseAssetId = altTradingPairs.find(
      (tradingPair) => tradingPair.id === tradingPairId,
    )?.baseAssetId;
    if (baseAssetId && assets?.[baseAssetId]) {
      return assets[baseAssetId].symbol;
    }
    return "";
  };

  const getQuoteAssetSymbol = (tradingPairId: number) => {
    const assets = data["assets"];
    if (altTradingPairs.length === 0) {
      return "";
    }
    const quoteAssetId = altTradingPairs.find(
      (tradingPair) => tradingPair.id === tradingPairId,
    )?.quoteAssetId;
    if (quoteAssetId && assets?.[quoteAssetId]) {
      return assets[quoteAssetId].symbol;
    }
    return "";
  };

  const permissions = useMemo(() => {
    return permissionsInfo?.permissions[0][1] || [];
  }, [permissionsInfo]);

  const hasInvestRoleOnly = () => {
    return (
      (permissions.includes("invest") || permissions.includes("ro:invest")) &&
      !permissions.includes("trader")
    );
  };

  const hasTraderRoleOnly = () => {
    return (
      permissions.includes("trader") &&
      (!permissions.includes("invest") || !permissions.includes("ro:invest"))
    );
  };

  const hasTraderAndInvestRoles = () => {
    return (
      permissions.includes("trader") &&
      (permissions.includes("invest") || permissions.includes("ro:invest"))
    );
  };

  const products = useMemo(() => {
    const tradingPairs: {
      [key: number]: TradingPair;
    } = data["trading-pairs"];
    const tradingPairItems: ProductItem[] = Object.values(tradingPairs || {})
      .filter((tp) => tp.tradingStatus === "Trading")
      .map((pairs) => {
        const tp = pairs;
        return {
          externalId: `TP-${tp.id}`,
          id: tp.id,
          type: "Trading Pair",
          name: tp.ticker,
          description: tp.description,
          baseAsset: tp.baseAsset,
          quoteAsset: tp.quoteAsset,
          productId: tp.productId,
          productType: productTypesMap?.[tp.productId]?.name || "Unknown",
          tradingPair: tp,
        };
      });
    const fundItems: ProductItem[] = funds.map((fund: Fund) => {
      return {
        externalId: `F-${fund.id}`,
        id: fund.id,
        type: "Fund",
        name: fund.name,
        description: isValidISIN(fund.meta.isin) ? fund.meta.isin : "",
        baseAsset: getBaseAssetSymbol(fund.tradingPairId),
        quoteAsset: getQuoteAssetSymbol(fund.tradingPairId),
        productId:
          tradingPairs?.[fund.tradingPairId]?.productId ||
          altTradingPairsMap?.[fund.tradingPairId]?.productId,
        productType:
          productTypesMap?.[
            tradingPairs?.[fund.tradingPairId]?.productId ||
              altTradingPairsMap?.[fund.tradingPairId]?.productId
          ]?.name || "Unknown",
        tradingPair:
          tradingPairs?.[fund.tradingPairId] ||
          altTradingPairsMap?.[fund.tradingPairId],
      };
    });

    tradingPairItems.sort((a, b) => a.name.localeCompare(b.name));
    fundItems.sort((a, b) => a.name.localeCompare(b.name));

    if (hasTraderAndInvestRoles()) {
      return [...tradingPairItems, ...fundItems];
    }
    if (hasInvestRoleOnly()) {
      return fundItems;
    }
    if (hasTraderRoleOnly()) {
      return tradingPairItems;
    }
    return [];
  }, [
    data["assets"],
    data["trading-pairs"],
    altTradingPairsMap,
    funds,
    productTypesMap,
  ]);

  const updateSelectedProduct = (
    productId: string,
    options?: { dontPersist?: boolean },
  ) => {
    if (!productId || products.length === 0) {
      return;
    }
    const productsMap = arrayToMap(products, "externalId");
    let product = productsMap[productId];

    if (!product) {
      product = products[0];
    }

    if (options?.dontPersist) return;

    setSelectedProduct(product);
    setBaseAsset(data["assets"]?.[product.tradingPair.baseAssetId] || null);
    setQuoteAsset(data["assets"]?.[product.tradingPair.quoteAssetId] || null);

    LocalStorage.setItem(
      localStorageKeys.lastProductSelected,
      String(productId),
    );
    if (product.type === "Trading Pair") {
      LocalStorage.setItem(
        localStorageKeys.lastTradingPairSelected,
        String(product.tradingPair.id),
      );
    }
  };

  const updateSelectedTradingPair = (
    tradingPairId: number,
    options?: { dontPersist?: boolean },
  ) => {
    if (options?.dontPersist) return;

    LocalStorage.setItem(
      localStorageKeys.lastTradingPairSelected,
      String(tradingPairId),
    );
  };

  return (
    <ProductSelectorContextProvider
      value={{
        products,
        tradingPairs: Object.values(data["trading-pairs"] || {}),
        altTradingPairs,
        funds,
        hasInvestRoleOnly,
        hasTraderRoleOnly,
        hasTraderAndInvestRoles,
        loading,
        updateSelectedProduct,
        updateSelectedTradingPair,
        selectedProduct,
        baseAsset,
        quoteAsset,
        tradingPair,
      }}
    >
      {children}
    </ProductSelectorContextProvider>
  );
};

const useProductSelector = () => {
  const {
    products,
    hasTraderAndInvestRoles,
    loading,
    updateSelectedProduct,
    updateSelectedTradingPair,
    selectedProduct,
    baseAsset,
    quoteAsset,
    tradingPair,
  } = useContext(ProductSelectorContext);

  return {
    products,
    hasTraderAndInvestRoles,
    loading,
    updateSelectedProduct,
    updateSelectedTradingPair,
    selectedProduct,
    baseAsset,
    quoteAsset,
    tradingPair,
  };
};

export { ProductSelectorProvider, useProductSelector };
